import { Button, Container, Paper, Stack, Typography } from "@mui/material"
import SuccessIcon from "@mui/icons-material/CheckCircle"
import { grey } from "@mui/material/colors"
import { TSaleStorage } from "../../../core/types/Sale"
import { useCheckoutContext } from "../../../core/hooks/useCheckoutContext"
import { IOrderCharge } from "../../../core/types/Order"
import { useCallback, useEffect, useState } from "react"
import OrderController from "../../../core/controllers/OrderController"
import { TProductDelivery } from "../../../core/types/Product"
import AsaasSeal from "../../../components/AsaasSeal"
import DeliveryInstructions from "../../../components/Checkout/DeliveryInstructions"
import { Trans, useTranslation } from "react-i18next"

interface OrderSuccessProps {
  sale: TSaleStorage | undefined
  isRedirect: boolean
  redirectSeconds: number
}

const OrderSuccess = ({
  sale,
  isRedirect,
  redirectSeconds,
}: OrderSuccessProps) => {
  const { productPrice } = useCheckoutContext()
  const { t } = useTranslation()

  let refreshChargeInterval: NodeJS.Timer | undefined = undefined

  const [charge, setCharge] = useState<IOrderCharge | undefined>(undefined)

  const isCommunityLinkLoaded =
    charge?.telegramCommunityInviteLinks &&
    charge.telegramCommunityInviteLinks.length > 0

  const refreshCharge = useCallback(async (chargeId?: string) => {
    if (refreshChargeInterval) {
      clearInterval(refreshChargeInterval)
    }

    refreshChargeInterval = setInterval(async () => {
      if (chargeId) {
        const charge = await OrderController.verified({ chargeId })
        setCharge(charge)
        if (
          charge?.telegramCommunityInviteLinks &&
          charge.telegramCommunityInviteLinks.length > 0
        ) {
          if (refreshChargeInterval) {
            clearInterval(refreshChargeInterval)
          }
        }
      }
    }, 2000)
  }, [])

  useEffect(() => {
    if (!isRedirect && sale?.chargeId) {
      refreshCharge(sale?.chargeId)
    }
  }, [isRedirect, sale?.chargeId, refreshCharge])

  return (
    <Container component={"main"} maxWidth="md">
      {!isRedirect && (
        <Stack direction="column" spacing={3} sx={{ pt: 8 }}>
          <Paper
            square={true}
            elevation={0}
            sx={{ width: "100%", borderRadius: 5 }}
          >
            <Stack
              direction="column"
              alignItems="center"
              justifyContent="center"
              width="100%"
              sx={{ py: { sm: 3, xs: 2 } }}
              spacing={2}
            >
              <Stack sx={{ px: { sm: 5, xs: 2 }, width: "100%" }}>
                <Paper
                  square={true}
                  elevation={0}
                  sx={{
                    width: "100%",
                    borderRadius: 1,
                    backgroundColor: "#1E88E5",
                  }}
                >
                  <Stack
                    direction="row"
                    spacing={2}
                    alignContent="center"
                    alignItems="center"
                    sx={{ py: 3, px: { sm: 5, xs: 1 } }}
                  >
                    <SuccessIcon sx={{ fontSize: "3.5em", color: "#212121" }} />
                    <Stack direction="column">
                      <Typography variant="h5">
                        {t("postPurchase.congratulations", { name: sale?.customer.name })}
                      </Typography>
                      <Typography variant="h4" fontWeight={600}>
                        {t("postPurchase.approved")}
                      </Typography>
                    </Stack>
                  </Stack>
                </Paper>
              </Stack>
              <Container maxWidth="sm">
                <DeliveryInstructions
                  items={sale?.items ?? []}
                  customerEmail={sale?.customer.email ?? ""}
                />

                <Paper
                  square={true}
                  elevation={0}
                  sx={{
                    width: "100%",
                    borderRadius: 1,
                    backgroundColor: grey[100],
                  }}
                >
                  <Stack
                    direction="row"
                    spacing={2}
                    alignContent="center"
                    alignItems="center"
                    justifyContent="center"
                    sx={{ p: 3 }}
                  >
                    <Typography variant="body1">
                      <Trans
                        i18nKey="postPurchase.orderNumber"
                        components={{ code: <strong>{sale?.orderCode}</strong> }}
                      />
                    </Typography>
                  </Stack>
                </Paper>
              </Container>
            </Stack>
          </Paper>

          {productPrice?.delivery === TProductDelivery.Community && (
            <Paper
              square={true}
              elevation={0}
              sx={{ width: "100%", borderRadius: 5, p: 3 }}
            >
              <Container maxWidth="sm">
                {!isCommunityLinkLoaded && (
                  <Stack
                    direction="column"
                    alignItems="center"
                    justifyContent="center"
                    width="100%"
                  >
                    <Typography variant="body1" sx={{ p: 3 }}>
                      {t("postPurchase.communityLoading")}
                    </Typography>
                  </Stack>
                )}
                {isCommunityLinkLoaded && (
                  <Stack
                    direction="column"
                    alignItems="center"
                    justifyContent="center"
                    width="100%"
                    spacing={2}
                  >
                    <Typography variant="h6" gutterBottom>
                      {t("postPurchase.communityAccess")}
                    </Typography>
                    {charge?.telegramCommunityInviteLinks?.map((link) => (
                      <Stack key={link.inviteLink} width="100%">
                        <Button
                          fullWidth
                          variant="contained"
                          href={link.inviteLink}
                          sx={{ textTransform: "none" }}
                        >
                          {link.name}
                        </Button>
                      </Stack>
                    ))}
                  </Stack>
                )}
              </Container>
            </Paper>
          )}

          <Stack
            direction="column"
            alignItems="center"
            justifyContent="center"
            width="100%"
            spacing={2}
          >
            <Container maxWidth="sm">
              <Stack direction="column" width="100%" sx={{ width: "100%" }}>
                <Typography variant="body1" fontWeight={600} gutterBottom>
                  {t("postPurchase.needHelp")}
                </Typography>
                <Typography variant="body1" sx={{ marginBottom: 3 }}>
                  {t("postPurchase.talkToSeller")}
                </Typography>

                <Typography variant="body1" gutterBottom>
                  <strong>{t("postPurchase.sellerName")}</strong>
                  {sale?.sellerName}
                </Typography>
                <Typography variant="body1">
                  <strong>{t("postPurchase.sellerEmail")}</strong>
                  {sale?.sellerEmail}
                </Typography>
              </Stack>
            </Container>
          </Stack>

          <AsaasSeal sx={{ pt: 3 }} />
        </Stack>
      )}

      {isRedirect && (
        <Stack
          direction="column"
          spacing={3}
          alignContent="center"
          alignItems="center"
          sx={{ pt: 8 }}
        >
          <Typography variant="body1" sx={{ mt: 2 }} data-cy="successRedirect">
            {t("postPurchase.redirecting", { seconds: redirectSeconds })}
          </Typography>
        </Stack>
      )}
    </Container>
  )
}

export default OrderSuccess
